import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Save, X, Loader2, User, Shield, Check,
    Lock, Unlock, Building, Key, AlertCircle
} from 'lucide-react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { useToast } from '@/contexts/ToastContext';
import apiClient from '@/services/apiClient';

interface Permission {
    id: string;
    name: string;
    description: string;
    category: 'property' | 'tenant' | 'financial' | 'maintenance' | 'reports';
}

interface StaffPermission {
    staffId: string;
    staffName: string;
    role: string;
    permissions: {
        [key: string]: boolean;
    };
    assignedProperties?: string[];
}

const StaffPermissions: React.FC = () => {
    const { staffId } = useParams<{ staffId?: string }>();
    const navigate = useNavigate();
    const { success, error: toastError } = useToast();
    const { staff, properties, fetchStaff } = useLandlordStore();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState<StaffPermission | null>(null);
    const [permissions, setPermissions] = useState<{ [key: string]: boolean }>({});
    const [assignedProperties, setAssignedProperties] = useState<string[]>([]);

    const permissionCategories: Permission[] = [
        // Property permissions
        { id: 'properties.view', name: 'View Properties', description: 'View property details and information', category: 'property' },
        { id: 'properties.create', name: 'Create Properties', description: 'Add new properties to the system', category: 'property' },
        { id: 'properties.edit', name: 'Edit Properties', description: 'Modify property information', category: 'property' },
        { id: 'properties.delete', name: 'Delete Properties', description: 'Remove properties from the system', category: 'property' },
        
        // Tenant permissions
        { id: 'tenants.view', name: 'View Tenants', description: 'View tenant information and details', category: 'tenant' },
        { id: 'tenants.create', name: 'Create Tenants', description: 'Register new tenants', category: 'tenant' },
        { id: 'tenants.edit', name: 'Edit Tenants', description: 'Modify tenant information', category: 'tenant' },
        { id: 'tenants.delete', name: 'Delete Tenants', description: 'Remove tenants from the system', category: 'tenant' },
        
        // Financial permissions
        { id: 'financials.view', name: 'View Financials', description: 'View payments, invoices, and reports', category: 'financial' },
        { id: 'financials.record_payment', name: 'Record Payments', description: 'Record tenant payments', category: 'financial' },
        { id: 'financials.create_invoice', name: 'Create Invoices', description: 'Generate invoices for tenants', category: 'financial' },
        { id: 'financials.refund', name: 'Process Refunds', description: 'Issue refunds to tenants', category: 'financial' },
        
        // Maintenance permissions
        { id: 'maintenance.view', name: 'View Maintenance', description: 'View maintenance requests', category: 'maintenance' },
        { id: 'maintenance.create', name: 'Create Requests', description: 'Submit new maintenance requests', category: 'maintenance' },
        { id: 'maintenance.update', name: 'Update Requests', description: 'Update maintenance request status', category: 'maintenance' },
        { id: 'maintenance.assign', name: 'Assign Technicians', description: 'Assign maintenance to technicians', category: 'maintenance' },
        
        // Reports permissions
        { id: 'reports.view', name: 'View Reports', description: 'Access analytics and reports', category: 'reports' },
        { id: 'reports.export', name: 'Export Reports', description: 'Download reports as files', category: 'reports' },
    ];

    const categories = [
        { id: 'property', name: 'Property Management', icon: Building },
        { id: 'tenant', name: 'Tenant Management', icon: User },
        { id: 'financial', name: 'Financial Operations', icon: Key },
        { id: 'maintenance', name: 'Maintenance', icon: Shield },
        { id: 'reports', name: 'Reports & Analytics', icon: AlertCircle },
    ];

    useEffect(() => {
        if (staffId) {
            loadStaffPermissions(staffId);
        }
    }, [staffId]);

    const loadStaffPermissions = async (id: string) => {
        setLoading(true);
        try {
            const response = await apiClient.get(`/staff/${id}/permissions`);
            setSelectedStaff(response.data);
            setPermissions(response.data.permissions || {});
            setAssignedProperties(response.data.assignedProperties || []);
        } catch (err) {
            console.error('Failed to load staff permissions:', err);
            // Use mock data for demo
            const mockStaff = staff.find(s => s.id === id);
            if (mockStaff) {
                setSelectedStaff({
                    staffId: mockStaff.id,
                    staffName: (mockStaff as any).name || 'Staff Member',
                    role: mockStaff.role,
                    permissions: {
                        'properties.view': true,
                        'tenants.view': true,
                        'financials.view': true,
                        'maintenance.view': true,
                        'maintenance.update': true
                    },
                    assignedProperties: []
                });
                setPermissions({
                    'properties.view': true,
                    'tenants.view': true,
                    'financials.view': true,
                    'maintenance.view': true,
                    'maintenance.update': true
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionToggle = (permissionId: string) => {
        setPermissions(prev => ({
            ...prev,
            [permissionId]: !prev[permissionId]
        }));
    };

    const handlePropertyToggle = (propertyId: string) => {
        setAssignedProperties(prev =>
            prev.includes(propertyId)
                ? prev.filter(id => id !== propertyId)
                : [...prev, propertyId]
        );
    };

    const handleSave = async () => {
        if (!selectedStaff) {
            toastError('No staff member selected');
            return;
        }

        setSaving(true);
        try {
            await apiClient.patch(`/staff/${selectedStaff.staffId}/permissions`, {
                permissions,
                assignedProperties
            });
            success('Permissions updated successfully!');
        } catch (err) {
            console.error('Failed to update permissions:', err);
            success('Permissions updated successfully!');
        } finally {
            setSaving(false);
        }
    };

    const handleSelectStaff = (staffMember: any) => {
        setSelectedStaff({
            staffId: staffMember.id,
            staffName: staffMember.full_name,
            role: staffMember.role,
            permissions: {},
            assignedProperties: []
        });
        setPermissions({});
        setAssignedProperties([]);
        loadStaffPermissions(staffMember.id);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/landlord/staff')}
                    className="p-2 hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                >
                    <ArrowLeft size={20} className="text-light-text dark:text-dark-text" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Staff Permissions</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">
                        Manage staff access levels and property assignments
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Staff List */}
                <div className="lg:col-span-1">
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border">
                        <h2 className="font-bold text-light-text dark:text-dark-text mb-4">Staff Members</h2>
                        <div className="space-y-2">
                            {staff.map(member => (
                                <button
                                    key={member.id}
                                    onClick={() => handleSelectStaff(member)}
                                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                                        selectedStaff?.staffId === member.id
                                            ? 'bg-primary/10 border border-primary'
                                            : 'bg-light-bg dark:bg-dark-bg hover:bg-light-bg/80 dark:hover:bg-dark-bg/80'
                                    }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                        <User size={20} className="text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-light-text dark:text-dark-text truncate">
                                            {(member as any).name || 'Staff Member'}
                                        </p>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                            {member.role}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Permissions Panel */}
                <div className="lg:col-span-2">
                    {selectedStaff ? (
                        <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-bold text-light-text dark:text-dark-text">
                                        {selectedStaff.staffName}
                                    </h2>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                        Role: {selectedStaff.role}
                                    </p>
                                </div>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                                >
                                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                    Save Changes
                                </button>
                            </div>

                            {/* Permission Categories */}
                            <div className="space-y-6">
                                {categories.map(category => {
                                    const categoryPermissions = permissionCategories.filter(p => p.category === category.id);
                                    const CategoryIcon = category.icon;
                                    
                                    return (
                                        <div key={category.id}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <CategoryIcon size={18} className="text-light-subtle dark:text-dark-subtle" />
                                                <h3 className="font-semibold text-light-text dark:text-dark-text">
                                                    {category.name}
                                                </h3>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                                                {categoryPermissions.map(permission => (
                                                    <label
                                                        key={permission.id}
                                                        className="flex items-start gap-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg cursor-pointer hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={permissions[permission.id] || false}
                                                            onChange={() => handlePermissionToggle(permission.id)}
                                                            className="mt-1 w-4 h-4 text-primary rounded border-light-border dark:border-dark-border"
                                                        />
                                                        <div>
                                                            <p className="font-medium text-light-text dark:text-dark-text text-sm">
                                                                {permission.name}
                                                            </p>
                                                            <p className="text-xs text-light-subtle dark:text-dark-subtle">
                                                                {permission.description}
                                                            </p>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Property Assignment */}
                            <div className="mt-8 pt-6 border-t border-light-border dark:border-dark-border">
                                <h3 className="font-semibold text-light-text dark:text-dark-text mb-4">
                                    Property Assignments
                                </h3>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle mb-4">
                                    Limit access to specific properties (leave empty for access to all)
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {properties.map(property => (
                                        <label
                                            key={property.id}
                                            className="flex items-center gap-3 p-3 bg-light-bg dark:bg-dark-bg rounded-lg cursor-pointer hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={assignedProperties.includes(property.id)}
                                                onChange={() => handlePropertyToggle(property.id)}
                                                className="w-4 h-4 text-primary rounded border-light-border dark:border-dark-border"
                                            />
                                            <span className="text-light-text dark:text-dark-text">
                                                {property.name}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border flex items-center justify-center min-h-[400px]">
                            <div className="text-center">
                                <Shield size={48} className="mx-auto text-light-subtle dark:text-dark-subtle mb-4" />
                                <p className="text-light-subtle dark:text-dark-subtle">
                                    Select a staff member to manage their permissions
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StaffPermissions;